import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Activity, Clock, Play, RefreshCw, Square, Trash2 } from 'lucide-react';
import {
  MetricsWindow,
  PerfRun,
  ScenariosResponse,
  cancelRun,
  createRun,
  getScenarios,
  getStreamUrl,
  listRuns,
  purgeRuns,
} from '@/services/perfTestsApi';
import { ScenarioPicker } from '@/components/perf/ScenarioPicker';
import { RunListTable } from '@/components/perf/RunListTable';
import { LiveMetricsChart } from '@/components/perf/LiveMetricsChart';
import { RunDetailDrawer } from '@/components/perf/RunDetailDrawer';
import { StackStatusPanel } from '@/components/perf/StackStatusPanel';
import { useEventSource } from '@/hooks/useEventSource';

export default function PerfTestsPage() {
  const [scenarios, setScenarios] = useState<ScenariosResponse | null>(null);
  const [runs, setRuns] = useState<PerfRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState('');
  const [selectedProfile, setSelectedProfile] = useState('');
  const [selectedTarget, setSelectedTarget] = useState('');
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [drawerRunId, setDrawerRunId] = useState<string | null>(null);
  const [windows, setWindows] = useState<MetricsWindow[]>([]);
  const [stackReady, setStackReady] = useState(false);

  const refreshAll = async () => {
    try {
      const [scn, lst] = await Promise.all([getScenarios(), listRuns({ limit: 50 })]);
      setScenarios(scn);
      setRuns(lst.runs);
      // Pré-sélection au 1er chargement
      if (!selectedScenario && scn.scenarios.length > 0) {
        const first = scn.scenarios[0];
        setSelectedScenario(first.name);
        setSelectedProfile(first.defaultProfile);
      }
      if (!selectedTarget && scn.allowedTargets.length > 0) {
        setSelectedTarget(scn.allowedTargets[0]);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erreur inconnue';
      toast.error(`Chargement échoué : ${message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh runs si l'un est en cours
  useEffect(() => {
    const hasActive = runs.some((r) => r.status === 'pending' || r.status === 'running');
    if (!hasActive) return;
    const interval = setInterval(async () => {
      try {
        const lst = await listRuns({ limit: 50 });
        setRuns(lst.runs);
      } catch {
        /* ignore */
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [runs]);

  const onScenarioChange = (name: string) => {
    setSelectedScenario(name);
    const def = scenarios?.scenarios.find((s) => s.name === name);
    if (def) setSelectedProfile(def.defaultProfile);
  };

  const onRun = async () => {
    if (!selectedScenario || !selectedProfile || !selectedTarget) return;
    setStarting(true);
    try {
      const { run } = await createRun({
        scenarioName: selectedScenario,
        profile: selectedProfile,
        targetUrl: selectedTarget,
      });
      toast.success(`Run démarré : ${run.scenario_name} (${run.profile})`);
      setSelectedRunId(run.id);
      await refreshAll();
    } catch (err) {
      const error = err as { response?: { status?: number; data?: { error?: string; code?: string } } };
      const msg = error.response?.data?.error ?? 'Erreur de démarrage';
      const code = error.response?.data?.code;
      if (error.response?.status === 409) {
        toast.error(`Un run est déjà en cours (${code})`);
      } else {
        toast.error(msg);
      }
    } finally {
      setStarting(false);
    }
  };

  const onPurge = async () => {
    if (runs.length === 0) return;
    if (!confirm(`Supprimer ${runs.length} run(s) de l'historique ? Les rapports HTML et samples NDJSON seront aussi effacés du disque. Cette action est irréversible.`)) return;
    try {
      const r = await purgeRuns();
      toast.success(`${r.deleted} run(s) supprimé(s)`);
      setSelectedRunId(null);
      setDrawerRunId(null);
      await refreshAll();
    } catch {
      toast.error('Purge échouée');
    }
  };

  const onCancel = async (runId: string) => {
    if (!confirm('Annuler ce run ?')) return;
    try {
      await cancelRun(runId);
      toast.success('Run annulé');
      await refreshAll();
    } catch {
      toast.error('Annulation échouée');
    }
  };

  const activeRun = runs.find((r) => r.status === 'pending' || r.status === 'running');

  // Reset windows quand on change de run actif
  useEffect(() => {
    if (!activeRun) return;
    setWindows([]);
  }, [activeRun?.id]);

  // Tick 1 s pour le décompte temps restant du run actif
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!activeRun) return;
    setNowMs(Date.now());
    const t = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [activeRun?.id]);

  const totalSec = activeRun ? scenarios?.profileDurationsSec?.[activeRun.profile] ?? null : null;
  const elapsedSec = activeRun
    ? Math.max(0, Math.floor((nowMs - new Date(activeRun.started_at).getTime()) / 1000))
    : 0;
  const remainingSec = totalSec !== null ? Math.max(0, totalSec - elapsedSec) : null;
  const progressPct = totalSec ? Math.min(100, (elapsedSec / totalSec) * 100) : 0;
  const fmtMmss = (s: number) =>
    `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

  // SSE stream pour le run actif
  useEventSource<MetricsWindow>(
    activeRun ? getStreamUrl(activeRun.id) : null,
    {
      onEvent: (name, data) => {
        if (name === 'window') {
          setWindows((prev) => [...prev.slice(-119), data]);
        }
      },
      onEnd: () => {
        // Refresh historique pour récupérer le summary final
        listRuns({ limit: 50 }).then((lst) => setRuns(lst.runs)).catch(() => { /* noop */ });
      },
    },
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <Activity className="w-5 h-5 text-indigo-600" />
            Tests de charge
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Pilote k6 contre une cible whitelist, voir les métriques live et l'historique.
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
        >
          <RefreshCw className="w-4 h-4" />
          Actualiser
        </button>
      </div>

      <StackStatusPanel onReadyChange={setStackReady} />

      {loading ? (
        <div className="text-sm text-gray-500">Chargement…</div>
      ) : (
        <>
          {/* Picker + bouton run */}
          <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
            <ScenarioPicker
              scenarios={scenarios?.scenarios ?? []}
              selectedScenario={selectedScenario}
              selectedProfile={selectedProfile}
              selectedTarget={selectedTarget}
              allowedTargets={scenarios?.allowedTargets ?? []}
              profileDurationsSec={scenarios?.profileDurationsSec}
              onScenarioChange={onScenarioChange}
              onProfileChange={setSelectedProfile}
              onTargetChange={setSelectedTarget}
              disabled={starting || !!activeRun}
            />
            <div className="flex items-center justify-between">
              <div className="text-xs text-gray-500">
                Caps : VUs ≤ {scenarios?.caps.maxVus ?? '?'},
                durée ≤ {(scenarios?.caps.maxDurationSeconds ?? 0) / 60} min,
                1 seul run en parallèle.
              </div>
              {activeRun ? (
                <button
                  onClick={() => onCancel(activeRun.id)}
                  className="px-3 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center gap-2"
                >
                  <Square className="w-4 h-4" />
                  Annuler le run en cours
                </button>
              ) : (
                <button
                  onClick={onRun}
                  disabled={starting || !selectedScenario || !selectedProfile || !selectedTarget || !stackReady}
                  title={!stackReady ? 'Stack pas prête : démarre-la avec npx nx run easyobeya_backend:perf-stack-up' : undefined}
                  className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  {starting ? 'Démarrage…' : 'Lancer'}
                </button>
              )}
            </div>
          </div>

          {/* Run actif : graphes live */}
          {activeRun && (
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-indigo-900">
                    Run actif : {activeRun.scenario_name} ({activeRun.profile})
                  </div>
                  <div className="text-xs text-indigo-700 mt-1">
                    Status : {activeRun.status} — cible : {activeRun.target_url}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-indigo-800 font-medium tabular-nums">
                  <Clock className="w-3.5 h-3.5" />
                  {totalSec !== null && remainingSec !== null
                    ? remainingSec > 0
                      ? <>{fmtMmss(remainingSec)} restant <span className="text-indigo-500 font-normal">({fmtMmss(elapsedSec)} / {fmtMmss(totalSec)})</span></>
                      : <>Finalisation… <span className="text-indigo-500 font-normal">({fmtMmss(elapsedSec)})</span></>
                    : <>Écoulé {fmtMmss(elapsedSec)}</>}
                </div>
              </div>
              {totalSec !== null && (
                <div className="h-1.5 bg-indigo-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-indigo-500 transition-[width] duration-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              )}
              <LiveMetricsChart windows={windows} targets={scenarios?.targets} />
            </div>
          )}

          {/* Historique */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-sm font-medium text-gray-700">Historique</h2>
              <button
                onClick={onPurge}
                disabled={runs.length === 0 || !!activeRun}
                title={activeRun ? "Annule le run actif d'abord" : runs.length === 0 ? 'Aucun run' : 'Supprimer tous les runs terminés (DB + fichiers)'}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Trash2 className="w-3 h-3" />
                Purger l'historique
              </button>
            </div>
            <RunListTable
              runs={runs}
              onRowClick={(r) => {
                setSelectedRunId(r.id);
                setDrawerRunId(r.id);
              }}
              selectedRunId={selectedRunId}
            />
          </div>
        </>
      )}

      <RunDetailDrawer
        runId={drawerRunId}
        onClose={() => setDrawerRunId(null)}
        onDeleted={() => {
          setSelectedRunId(null);
          refreshAll();
        }}
      />
    </div>
  );
}
